'use client';

import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import { formatPlanPriceBrlFromCents } from '@/core/domain';
import {
  POST_ONBOARDING_PLAN_FEATURES,
  POST_ONBOARDING_PLAN_OFFERS,
} from '@/features/plans/presentation/constants/plan-offers-display';
import {
  LANDING_PLANS_AFTER_GRID_CTA_LABEL,
  LANDING_PLANS_SECTION_BADGE,
  LANDING_PLANS_SECTION_SUBTITLE,
  LANDING_PLANS_SECTION_TITLE_GRADIENT,
  LANDING_PLANS_SECTION_TITLE_LEAD,
  LANDING_PLANS_TRIAL_BODY,
  LANDING_PLANS_TRIAL_TITLE,
} from '@/features/home/constants/landing-plans-section-copy';

const PlansSection: React.FC = () => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            el.classList.remove('reveal-hidden');
            el.classList.add('reveal-visible');
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.08 }
    );

    if (contentRef.current) observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="planos" className="section-padding animated-gradient-bg relative overflow-hidden">
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2"
        style={{
          background: 'radial-gradient(circle, rgba(126,207,239,0.1) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />

      <div className="relative z-10 mx-auto max-w-[1320px]">
        <div ref={contentRef} className="reveal-hidden">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <span className="tag-badge mb-5 inline-block border border-cyan/30 bg-cyan/15 text-cyan">
              {LANDING_PLANS_SECTION_BADGE}
            </span>
            <h2 className="mb-5 font-fraunces text-4xl font-light leading-[1.1] tracking-tight text-white md:text-5xl lg:text-6xl">
              {LANDING_PLANS_SECTION_TITLE_LEAD}{' '}
              <span className="italic gradient-text">{LANDING_PLANS_SECTION_TITLE_GRADIENT}</span>
            </h2>
            <p className="mb-8 text-lg font-light leading-relaxed text-white/60">{LANDING_PLANS_SECTION_SUBTITLE}</p>

            <ul className="mx-auto max-w-2xl space-y-2 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-left text-sm text-white/90">
              {POST_ONBOARDING_PLAN_FEATURES.map((line) => (
                <li key={line} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan" aria-hidden />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-3 lg:gap-4">
            {POST_ONBOARDING_PLAN_OFFERS.map((offer) => (
              <div
                key={offer.id}
                className={`relative flex flex-col rounded-xl border p-5 sm:p-6 ${
                  offer.highlight
                    ? 'border-cyan/60 bg-white/10 shadow-lg shadow-cyan/10 ring-1 ring-cyan/30'
                    : 'border-white/15 bg-white/5'
                }`}
              >
                {offer.highlight && offer.highlightLabel ? (
                  <span className="mb-3 inline-flex w-fit rounded-full bg-cyan/25 px-2.5 py-1 text-xs font-semibold text-cyan-light">
                    {offer.highlightLabel}
                  </span>
                ) : null}
                {!offer.highlight && offer.highlightLabel ? (
                  <span className="mb-3 inline-flex w-fit rounded-full bg-white/10 px-2.5 py-1 text-xs font-medium text-white/80">
                    {offer.highlightLabel}
                  </span>
                ) : null}

                <h3 className="font-fraunces text-lg font-semibold text-white">{offer.title}</h3>

                <p className="mt-3 flex flex-wrap items-baseline gap-1">
                  <span className="text-3xl font-bold text-white">{offer.priceLine}</span>
                  <span className="text-sm text-white/70">{offer.priceSub}</span>
                </p>
                <p className="mt-1 text-xs text-white/60">{offer.billingNote}</p>

                {offer.equivalentMonthlyCents != null ? (
                  <div className="mt-3 space-y-2 rounded-lg border border-cyan/25 bg-cyan/10 px-3 py-2.5 text-xs leading-snug text-cyan-light">
                    <p className="font-medium text-white">
                      Equivale a{' '}
                      <strong>{formatPlanPriceBrlFromCents(offer.equivalentMonthlyCents)}</strong>
                      /mês no período
                    </p>
                    {offer.savingsTotalCents != null && offer.savingsTotalCents > 0 ? (
                      <p className="text-white/90">
                        Economia de{' '}
                        <strong className="text-cyan-light">
                          {formatPlanPriceBrlFromCents(offer.savingsTotalCents)}
                        </strong>
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div
            className="mt-10 rounded-xl border border-cyan/25 bg-white/[0.06] px-4 py-4 sm:px-5"
            role="region"
            aria-label="Teste grátis por 7 dias"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cyan/15 text-cyan-light">
                <Sparkles className="h-5 w-5" aria-hidden />
              </div>
              <div className="space-y-1 text-sm text-white/90">
                <p className="font-semibold text-white">{LANDING_PLANS_TRIAL_TITLE}</p>
                <p className="text-xs leading-relaxed text-white/75 sm:text-sm">{LANDING_PLANS_TRIAL_BODY}</p>
              </div>
            </div>
          </div>

          <div className="mt-10 flex justify-center">
            <Link
              to="/login"
              className="btn-primary glow-pulse inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-sm font-bold sm:gap-3 sm:px-10 sm:py-4 sm:text-base"
            >
              {LANDING_PLANS_AFTER_GRID_CTA_LABEL}
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlansSection;
